import React, { useEffect, useState, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import paymentsService from '../../services/payments.service';
import { GraficaPagosMensuales } from '../../types';

// Registrar los componentes necesarios de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Nombres de los meses en español
const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const PagosMensualesChart: React.FC = () => {
  const [chartData, setChartData] = useState<GraficaPagosMensuales | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anio, setAnio] = useState(new Date().getFullYear());

  // Obtener datos de la API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await paymentsService.getGraficaPagosMensuales(anio);
      setChartData(data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar datos de la gráfica:', err);
      setError('No se pudieron cargar los datos de la gráfica');
    } finally {
      setLoading(false);
    }
  }, [anio]);

  // Cargar datos al montar el componente o cambiar el año
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Preparar datos para la gráfica
  const getChartData = (): ChartData<'bar'> => {
    if (!chartData) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: meses,
      datasets: [
        {
          label: 'Pagos Reales',
          data: chartData.datos.map(item => item.totalPagosReales),
          backgroundColor: 'rgba(54, 162, 235, 0.7)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
        {
          label: 'Pagos Programados',
          data: chartData.datos.map(item => item.totalPagosProgramados),
          backgroundColor: 'rgba(255, 99, 132, 0.7)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Opciones de la gráfica
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `Pagos Mensuales - ${anio}`,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return new Intl.NumberFormat('es-CO', {
              style: 'currency',
              currency: 'COP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          }
        }
      }
    }
  };

  // Manejar cambio de año
  const handleAnioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAnio(parseInt(e.target.value));
  };

  // Generar opciones de años (últimos 5 años y próximos 2)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }

  if (loading) {
    return <div className="p-4 text-center">Cargando datos de pagos...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Resumen de Pagos Mensuales</h2>
        <div className="flex items-center">
          <label htmlFor="anio" className="mr-2 text-sm font-medium text-gray-700">
            Año:
          </label>
          <select
            id="anio"
            value={anio}
            onChange={handleAnioChange}
            className="border rounded px-2 py-1 text-sm"
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="h-80">
        <Bar data={getChartData()} options={options} />
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>Comparativa entre pagos reales y programados por mes.</p>
      </div>
    </div>
  );
};

export default PagosMensualesChart;
